import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { TrendingUp, TrendingDown, Sparkles, BookOpen, X, Filter, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DiscoverInvestments({ user, onLogout }) {
  const navigate = useNavigate();
  const [indices, setIndices] = useState([]);
  const [marketMovers, setMarketMovers] = useState({ top_gainers: [], top_losers: [], most_active: [] });
  const [sectors, setSectors] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [educationModalOpen, setEducationModalOpen] = useState(false);
  const [educationContent, setEducationContent] = useState({ title: '', content: '' });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled([
        axios.get(`${API}/discover/indices`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/discover/market-movers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/discover/sectors`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/watchlist`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      // Handle each result individually
      if (results[0].status === 'fulfilled') {
        setIndices(results[0].value.data.indices || []);
      } else {
        console.error('Error fetching indices:', results[0].reason);
      }
      
      if (results[1].status === 'fulfilled') {
        setMarketMovers(results[1].value.data || { top_gainers: [], top_losers: [], most_active: [] });
      } else {
        console.error('Error fetching market movers:', results[1].reason);
        setMarketMovers({ top_gainers: [], top_losers: [], most_active: [] });
      }
      
      if (results[2].status === 'fulfilled') {
        setSectors(results[2].value.data.sectors || []);
      } else {
        console.error('Error fetching sectors:', results[2].reason);
      }
      
      if (results[3].status === 'fulfilled') {
        setWatchlist(results[3].value.data.watchlist || []);
      } else {
        console.error('Error fetching watchlist:', results[3].reason);
      }
    } catch (error) {
      console.error('Critical error in fetchAllData:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromWatchlist = async (symbol) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/watchlist/${symbol}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWatchlist(watchlist.filter(s => s.symbol !== symbol));
    } catch (error) {
      console.error('Error removing from watchlist:', error);
    }
  };

  const openEducationModal = (type) => {
    const content = educationalContent[type];
    if (content) {
      setEducationContent(content);
      setEducationModalOpen(true);
    }
  };

  const educationalContent = {
    nifty50: {
      title: "Complete Guide to Nifty 50",
      content: (
        <div className="space-y-6 text-gray-700 dark:text-gray-300">
          <div>
            <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">What is Nifty 50?</h4>
            <p className="mb-3">
              Nifty 50 is India's premier stock market index representing the top 50 companies listed on the National Stock Exchange (NSE). 
              Think of it as a "report card" of India's stock market - when Nifty goes up, it means the overall market is doing well.
            </p>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-l-4 border-blue-500">
            <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">📊 Quick Facts:</p>
            <ul className="space-y-1 text-sm">
              <li>• Covers 66% of NSE's total market cap</li>
              <li>• Base date: Nov 3, 1995 (Base value: 1,000)</li>
              <li>• Current value: ~25,900 (as of Nov 2025)</li>
              <li>• Rebalanced twice yearly (June & December)</li>
              <li>• Represents 13 sectors of Indian economy</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">📈 How is Nifty Calculated?</h4>
            <p className="mb-2">Nifty uses <strong>Free Float Market Capitalization</strong> method:</p>
            <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg mb-3">
              <code className="text-sm">
                Nifty = (Current Market Value / Base Market Capital) × 1000
              </code>
            </div>
            <p className="text-sm italic">
              <strong>Example:</strong> If a company has 100 crore shares at ₹500 each, but only 60% are available for trading (free float), 
              only ₹30,000 crore counts towards Nifty calculation, not the full ₹50,000 crore.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">🏆 Top 10 Companies in Nifty 50 (by weightage)</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between p-2 bg-white dark:bg-gray-700 rounded">
                <span>1. Reliance Industries</span><span className="font-semibold">~10%</span>
              </div>
              <div className="flex justify-between p-2 bg-white dark:bg-gray-700 rounded">
                <span>2. HDFC Bank</span><span className="font-semibold">~8%</span>
              </div>
              <div className="flex justify-between p-2 bg-white dark:bg-gray-700 rounded">
                <span>3. ICICI Bank</span><span className="font-semibold">~7%</span>
              </div>
              <div className="flex justify-between p-2 bg-white dark:bg-gray-700 rounded">
                <span>4. Infosys</span><span className="font-semibold">~6%</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">💰 How to Invest in Nifty 50?</h4>
            <div className="space-y-3">
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">1. Index Funds (Easiest)</p>
                <p className="text-sm mb-2">Mutual funds that track Nifty 50. Low expense ratio (0.1-0.5%).</p>
                <p className="text-sm"><strong>Popular options:</strong> ICICI Pru Nifty 50, HDFC Index Nifty 50, UTI Nifty 50</p>
              </div>
              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">2. Nifty 50 ETFs</p>
                <p className="text-sm mb-2">Trade like stocks on the exchange. Real-time buying/selling.</p>
                <p className="text-sm"><strong>Popular options:</strong> Nippon India ETF Nifty 50, SBI ETF Nifty 50</p>
              </div>
              <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">3. Buy Individual Stocks</p>
                <p className="text-sm">Purchase all 50 stocks individually (requires significant capital).</p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">📊 Historical Performance</h4>
            <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg space-y-2 text-sm">
              <div className="flex justify-between"><span>Last 1 year:</span><span className="text-green-600 font-semibold">+15-20%</span></div>
              <div className="flex justify-between"><span>Last 5 years (CAGR):</span><span className="text-green-600 font-semibold">~12-15%</span></div>
              <div className="flex justify-between"><span>Last 10 years (CAGR):</span><span className="text-green-600 font-semibold">~13-16%</span></div>
              <p className="text-xs italic mt-2">*Past performance doesn't guarantee future returns</p>
            </div>
          </div>

          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border-l-4 border-yellow-500">
            <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">⚠️ Common Mistakes to Avoid:</p>
            <ul className="space-y-1 text-sm">
              <li>✗ Trying to "time the market" - invest regularly via SIP instead</li>
              <li>✗ Comparing Nifty with Sensex directly - they use different methodologies</li>
              <li>✗ Expecting guaranteed returns - markets are volatile</li>
              <li>✗ Investing lump sum at market peak - use rupee cost averaging</li>
            </ul>
          </div>

          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border-l-4 border-green-500">
            <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">✅ Pro Tips for Beginners:</p>
            <ul className="space-y-2 text-sm">
              <li><strong>1. Start with SIP:</strong> Invest ₹1,000-5,000 monthly in Nifty 50 index fund</li>
              <li><strong>2. Long-term horizon:</strong> Stay invested for 5+ years for best results</li>
              <li><strong>3. Low cost matters:</strong> Choose funds with expense ratio below 0.5%</li>
              <li><strong>4. Auto-invest:</strong> Set up automatic SIP to avoid emotional decisions</li>
              <li><strong>5. Review annually:</strong> Check performance once a year, not daily</li>
            </ul>
          </div>
        </div>
      )
    },
    pickStocks: {
      title: "Complete Stock Picking Guide",
      content: (
        <div className="space-y-6 text-gray-700 dark:text-gray-300">
          <div>
            <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">Step-by-Step Stock Picking Framework</h4>
            <p>Selecting winning stocks requires analyzing both numbers (quantitative) and quality (qualitative). Here's a proven 5-step process:</p>
          </div>

          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">📊 Step 1: Understand Key Financial Metrics</h4>
            
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">P/E Ratio (Price to Earnings)</p>
                <p className="text-sm mb-2"><strong>Formula:</strong> Stock Price ÷ Earnings Per Share (EPS)</p>
                <p className="text-sm mb-2"><strong>What it means:</strong> How much you pay for ₹1 of company earnings</p>
                <div className="bg-white dark:bg-gray-700 p-3 rounded mt-2">
                  <p className="text-sm font-semibold mb-1">Example Calculation:</p>
                  <p className="text-sm">TCS trades at ₹3,500, EPS = ₹120</p>
                  <p className="text-sm">P/E = 3,500 ÷ 120 = <strong>29.2</strong></p>
                  <p className="text-sm mt-2 text-green-600">✓ Compare with sector avg (IT sector avg: 25-30)</p>
                </div>
                <div className="mt-3 space-y-1 text-sm">
                  <p><strong>Good P/E:</strong> 15-25 (varies by sector)</p>
                  <p><strong>High P/E (>30):</strong> Market expects high growth OR overvalued</p>
                  <p><strong>Low P/E (&lt;15):</strong> Undervalued OR company has issues</p>
                </div>
              </div>

              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">ROE (Return on Equity)</p>
                <p className="text-sm mb-2"><strong>Formula:</strong> (Net Profit ÷ Shareholders Equity) × 100</p>
                <p className="text-sm mb-2"><strong>What it means:</strong> How efficiently company uses shareholder money</p>
                <div className="bg-white dark:bg-gray-700 p-3 rounded mt-2">
                  <p className="text-sm font-semibold mb-1">Example:</p>
                  <p className="text-sm">Company A: Net Profit ₹1,000 Cr, Equity ₹5,000 Cr</p>
                  <p className="text-sm">ROE = (1,000 ÷ 5,000) × 100 = <strong>20%</strong></p>
                </div>
                <div className="mt-3 space-y-1 text-sm">
                  <p className="text-green-600">✓ Excellent: >20%</p>
                  <p className="text-green-600">✓ Good: 15-20%</p>
                  <p className="text-orange-600">⚠ Average: 10-15%</p>
                  <p className="text-red-600">✗ Poor: &lt;10%</p>
                </div>
              </div>

              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Debt-to-Equity Ratio</p>
                <p className="text-sm mb-2"><strong>Formula:</strong> Total Debt ÷ Shareholders Equity</p>
                <p className="text-sm mb-2"><strong>What it means:</strong> Company's financial leverage and risk</p>
                <div className="bg-white dark:bg-gray-700 p-3 rounded mt-2">
                  <p className="text-sm font-semibold mb-1">Example:</p>
                  <p className="text-sm">Total Debt: ₹2,000 Cr, Equity: ₹3,000 Cr</p>
                  <p className="text-sm">D/E = 2,000 ÷ 3,000 = <strong>0.67</strong> (Good!)</p>
                </div>
                <div className="mt-3 space-y-1 text-sm">
                  <p className="text-green-600">✓ Safe: &lt;0.5</p>
                  <p className="text-green-600">✓ Acceptable: 0.5-1.0</p>
                  <p className="text-orange-600">⚠ Risky: 1.0-2.0</p>
                  <p className="text-red-600">✗ Very Risky: >2.0</p>
                </div>
              </div>

              <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Dividend Yield</p>
                <p className="text-sm mb-2"><strong>Formula:</strong> (Annual Dividend per Share ÷ Stock Price) × 100</p>
                <p className="text-sm mb-2"><strong>What it means:</strong> Cash return you get annually</p>
                <div className="bg-white dark:bg-gray-700 p-3 rounded mt-2">
                  <p className="text-sm font-semibold mb-1">Example:</p>
                  <p className="text-sm">ITC pays ₹12 dividend, stock price ₹450</p>
                  <p className="text-sm">Dividend Yield = (12 ÷ 450) × 100 = <strong>2.67%</strong></p>
                </div>
                <div className="mt-3 space-y-1 text-sm">
                  <p className="text-green-600">✓ High yield: >4% (PSUs, mature companies)</p>
                  <p>✓ Average: 1-3% (growth companies)</p>
                  <p>✓ Zero dividend: Growth/tech companies reinvest profits</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">🎯 Step 2: Qualitative Analysis Checklist</h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-3 p-3 bg-white dark:bg-gray-700 rounded">
                <input type="checkbox" className="mt-1" />
                <div>
                  <p className="font-semibold text-sm">Business Model - Do you understand it?</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Can you explain in simple terms how the company makes money?</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 bg-white dark:bg-gray-700 rounded">
                <input type="checkbox" className="mt-1" />
                <div>
                  <p className="font-semibold text-sm">Competitive Advantage (Moat)</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Brand power? Patents? Network effects? High switching costs?</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 bg-white dark:bg-gray-700 rounded">
                <input type="checkbox" className="mt-1" />
                <div>
                  <p className="font-semibold text-sm">Management Quality</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Check promoter holding (>50% good), track record, corporate governance</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 bg-white dark:bg-gray-700 rounded">
                <input type="checkbox" className="mt-1" />
                <div>
                  <p className="font-semibold text-sm">Industry Trends</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Is the sector growing? Declining? Facing disruption?</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 bg-white dark:bg-gray-700 rounded">
                <input type="checkbox" className="mt-1" />
                <div>
                  <p className="font-semibold text-sm">Revenue Growth Consistency</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Check 3-5 year revenue growth - look for 10-15% CAGR</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">💡 Step 3: Real Stock Comparison Example</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border dark:border-gray-600">
                <thead className="bg-gray-100 dark:bg-gray-700">
                  <tr>
                    <th className="p-2 text-left">Metric</th>
                    <th className="p-2 text-center">Stock A (Good)</th>
                    <th className="p-2 text-center">Stock B (Risky)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t dark:border-gray-600">
                    <td className="p-2">P/E Ratio</td>
                    <td className="p-2 text-center text-green-600">22</td>
                    <td className="p-2 text-center text-red-600">45</td>
                  </tr>
                  <tr className="border-t dark:border-gray-600">
                    <td className="p-2">ROE</td>
                    <td className="p-2 text-center text-green-600">18%</td>
                    <td className="p-2 text-center text-red-600">8%</td>
                  </tr>
                  <tr className="border-t dark:border-gray-600">
                    <td className="p-2">Debt/Equity</td>
                    <td className="p-2 text-center text-green-600">0.4</td>
                    <td className="p-2 text-center text-red-600">2.1</td>
                  </tr>
                  <tr className="border-t dark:border-gray-600">
                    <td className="p-2">5Y Revenue Growth</td>
                    <td className="p-2 text-center text-green-600">15% CAGR</td>
                    <td className="p-2 text-center text-red-600">3% CAGR</td>
                  </tr>
                  <tr className="border-t dark:border-gray-600">
                    <td className="p-2">Promoter Holding</td>
                    <td className="p-2 text-center text-green-600">52%</td>
                    <td className="p-2 text-center text-orange-600">35%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm mt-3 italic">Stock A shows better fundamentals across all metrics - lower risk, stronger growth, good management control.</p>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border-l-4 border-red-500">
            <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">❌ Common Stock Picking Mistakes:</p>
            <ul className="space-y-2 text-sm">
              <li><strong>1. Following tips blindly:</strong> Never buy based on WhatsApp/Telegram tips</li>
              <li><strong>2. Ignoring debt:</strong> High debt companies can collapse in downturns</li>
              <li><strong>3. Buying expensive stocks:</strong> High P/E without growth = bad investment</li>
              <li><strong>4. No position sizing:</strong> Never put >5-10% in one stock</li>
              <li><strong>5. Emotional decisions:</strong> Don't panic sell or FOMO buy</li>
            </ul>
          </div>

          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border-l-4 border-green-500">
            <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">✅ 5-Step Stock Selection Process:</p>
            <ol className="space-y-2 text-sm list-decimal pl-5">
              <li><strong>Screen stocks:</strong> Use filters (ROE &gt;15%, P/E &lt;25, Debt/Equity &lt;1)</li>
              <li><strong>Check fundamentals:</strong> Verify revenue growth, profit margins</li>
              <li><strong>Read annual reports:</strong> Understand business model and risks</li>
              <li><strong>Compare with peers:</strong> How does it stack up in its sector?</li>
              <li><strong>Set entry price:</strong> Don't overpay - wait for good valuation</li>
            </ol>
          </div>

          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border-l-4 border-amber-500">
            <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">⚠️ Golden Rules:</p>
            <ul className="space-y-1 text-sm">
              <li>• Invest only in businesses you understand</li>
              <li>• Never invest more than 10% of portfolio in a single stock</li>
              <li>• Always have a 3-5 year investment horizon</li>
              <li>• Set stop-loss at 15-20% below purchase price</li>
              <li>• Review your stocks quarterly, not daily</li>
            </ul>
          </div>
        </div>
      )
    },
    diversification: {
      title: "Complete Portfolio Diversification Guide",
      content: (
        <div className="space-y-6 text-gray-700 dark:text-gray-300">
          <div>
            <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">Why Diversification Matters</h4>
            <p className="mb-3">
              "Don't put all your eggs in one basket" - Diversification is the ONLY free lunch in investing. 
              It reduces risk without sacrificing returns by spreading investments across different assets.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded">
                <p className="font-semibold text-sm mb-1">❌ Not Diversified</p>
                <p className="text-xs">100% in one stock - if it drops 50%, your portfolio drops 50%</p>
              </div>
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded">
                <p className="font-semibold text-sm mb-1">✅ Well Diversified</p>
                <p className="text-xs">10 stocks - if one drops 50%, portfolio only drops 5%</p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">📊 The 4 Pillars of Diversification</h4>
            
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">1. Across Sectors</p>
                <p className="text-sm mb-3">Don't invest only in IT or Banking. Spread across different industries:</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <span className="w-3 h-3 bg-blue-500 rounded"></span>
                    <span>IT Services (15-20%)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="w-3 h-3 bg-green-500 rounded"></span>
                    <span>Banking & Finance (15-20%)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="w-3 h-3 bg-yellow-500 rounded"></span>
                    <span>FMCG (10-15%)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="w-3 h-3 bg-red-500 rounded"></span>
                    <span>Pharma (10-15%)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="w-3 h-3 bg-purple-500 rounded"></span>
                    <span>Energy (10-15%)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="w-3 h-3 bg-orange-500 rounded"></span>
                    <span>Auto (10-15%)</span>
                  </div>
                </div>
                <p className="text-xs mt-3 italic">Why? When IT is down, FMCG might be up. Different sectors perform differently in different economic cycles.</p>
              </div>

              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">2. Across Market Caps</p>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-semibold">Large Cap (₹20,000+ Cr)</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Stable, lower risk - Reliance, TCS, HDFC</p>
                    </div>
                    <span className="text-2xl font-bold text-green-600">60%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-semibold">Mid Cap (₹5,000-20,000 Cr)</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Balanced risk-reward - growing companies</p>
                    </div>
                    <span className="text-2xl font-bold text-orange-600">25%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-semibold">Small Cap (&lt;₹5,000 Cr)</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">High risk, high potential - aggressive growth</p>
                    </div>
                    <span className="text-2xl font-bold text-red-600">15%</span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">3. Across Asset Classes</p>
                <p className="text-sm mb-3">Don't put 100% in stocks. Balance with other asset classes:</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center p-2 bg-white dark:bg-gray-700 rounded">
                    <span>Equity (Stocks/MF)</span>
                    <span className="font-semibold">50-70%</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-white dark:bg-gray-700 rounded">
                    <span>Debt (FDs/Bonds/Debt Funds)</span>
                    <span className="font-semibold">20-30%</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-white dark:bg-gray-700 rounded">
                    <span>Gold (Physical/Gold Bonds/Gold ETF)</span>
                    <span className="font-semibold">5-10%</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-white dark:bg-gray-700 rounded">
                    <span>Cash/Emergency Fund</span>
                    <span className="font-semibold">5-10%</span>
                  </div>
                </div>
                <p className="text-xs mt-3 italic">Adjust based on age: Young (70% equity), Middle-age (60% equity), Retired (40% equity)</p>
              </div>

              <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">4. Geographic Diversification</p>
                <p className="text-sm mb-3">Don't invest only in India. Get global exposure:</p>
                <ul className="space-y-1 text-sm">
                  <li>• India stocks/mutual funds: 70-80%</li>
                  <li>• US stocks (Nasdaq 100, S&P 500): 10-15%</li>
                  <li>• Emerging markets funds: 5-10%</li>
                  <li>• Global diversified funds: 5-10%</li>
                </ul>
                <p className="text-xs mt-2 italic">Options: Motilal Oswal Nasdaq 100, Parag Parikh Flexi Cap (has foreign stocks)</p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">🎯 Sample Diversified Portfolio</h4>
            <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <p className="font-semibold mb-3 text-gray-800 dark:text-gray-100">Portfolio Value: ₹10,00,000</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Nifty 50 Index Fund</span>
                  <span className="font-semibold">₹3,00,000 (30%)</span>
                </div>
                <div className="flex justify-between">
                  <span>Large Cap Stocks (5 stocks)</span>
                  <span className="font-semibold">₹2,00,000 (20%)</span>
                </div>
                <div className="flex justify-between">
                  <span>Mid Cap Mutual Fund</span>
                  <span className="font-semibold">₹1,50,000 (15%)</span>
                </div>
                <div className="flex justify-between">
                  <span>Debt Mutual Funds</span>
                  <span className="font-semibold">₹2,00,000 (20%)</span>
                </div>
                <div className="flex justify-between">
                  <span>Gold ETF</span>
                  <span className="font-semibold">₹75,000 (7.5%)</span>
                </div>
                <div className="flex justify-between">
                  <span>International Fund (US stocks)</span>
                  <span className="font-semibold">₹75,000 (7.5%)</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">📅 Rebalancing Strategy</h4>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <p className="text-sm mb-3"><strong>Why rebalance?</strong> Over time, some assets grow faster, throwing off your allocation.</p>
              <p className="text-sm mb-3"><strong>Example:</strong> Your equity allocation grows from 60% to 75% due to bull market. Now you're taking too much risk!</p>
              <div className="space-y-2 text-sm">
                <p className="font-semibold">Rebalancing Rules:</p>
                <ul className="space-y-1 pl-5 list-disc">
                  <li>Review portfolio every 6 months or annually</li>
                  <li>If any asset class deviates by >5-10%, rebalance</li>
                  <li>Sell high-performing assets, buy underperforming ones</li>
                  <li>This enforces "buy low, sell high" discipline</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border-l-4 border-red-500">
            <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">❌ Over-Diversification Trap:</p>
            <ul className="space-y-2 text-sm">
              <li><strong>Too many stocks:</strong> Owning 50+ stocks = basically an index fund with more effort</li>
              <li><strong>Sweet spot:</strong> 10-20 stocks is optimal for retail investors</li>
              <li><strong>Can't track:</strong> If you own too many, you can't monitor them properly</li>
              <li><strong>Diminishing returns:</strong> After 15-20 stocks, diversification benefits plateau</li>
            </ul>
          </div>

          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border-l-4 border-green-500">
            <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">✅ Diversification Action Plan:</p>
            <ol className="space-y-2 text-sm list-decimal pl-5">
              <li><strong>Check current allocation:</strong> What % is in each sector/asset class?</li>
              <li><strong>Identify gaps:</strong> Too concentrated in one sector? Missing asset classes?</li>
              <li><strong>Set target allocation:</strong> Use age-based rule: (100 - Age) = % in equity</li>
              <li><strong>Invest systematically:</strong> Use SIP to gradually build diversified portfolio</li>
              <li><strong>Review & rebalance:</strong> Set calendar reminder for 6-month review</li>
            </ol>
          </div>

          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border-l-4 border-yellow-500">
            <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">💡 Pro Tips:</p>
            <ul className="space-y-1 text-sm">
              <li>• Start with index funds (instant diversification)</li>
              <li>• Add individual stocks only after building index fund core</li>
              <li>• Never have >10% in one stock, >25% in one sector</li>
              <li>• Increase equity allocation when markets crash (buy low!)</li>
              <li>• Keep 6 months expenses as emergency fund - never invest it</li>
            </ul>
          </div>
        </div>
      )
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 lg:p-8">
        <Navigation user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <Navigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100">Markets</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 ml-15">Explore stocks, analyze markets, and discover investment opportunities</p>
        </div>

        {/* Watchlist */}
        {watchlist.length > 0 && (
          <Card id="watchlist-section" className="glass p-6 mb-8 dark:bg-gray-800">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
              <Star className="w-5 h-5 mr-2 text-yellow-500 fill-yellow-500" />
              My Watchlist
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {watchlist.map((stock, idx) => (
                <div key={idx} className="p-4 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveFromWatchlist(stock.symbol)}
                    className="absolute top-2 right-2"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                  <p className="font-semibold text-gray-800 dark:text-gray-100">{stock.name}</p>
                  <p className="text-2xl font-bold text-gray-800 dark:text-gray-100 mt-2">₹{stock.price}</p>
                  <p className={`text-sm ${stock.change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {stock.change_percent >= 0 ? '+' : ''}{stock.change_percent}%
                  </p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Market Overview */}
        <Card className="glass p-6 mb-8 dark:bg-gray-800">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6 flex items-center">
            <Sparkles className="w-5 h-5 mr-2 text-purple-600 dark:text-purple-400" />
            Market Overview
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {indices.map((index, idx) => (
              <div key={idx} className="p-5 bg-gradient-to-br from-white to-gray-50 dark:from-gray-700 dark:to-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-600">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{index.name}</p>
                <p className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                  {index.value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <div className="flex items-center space-x-2">
                  {index.trend === 'up' ? (
                    <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                  ) : (
                    <TrendingDown className="w-5 h-5 text-red-600 dark:text-red-400" />
                  )}
                  <span className={`text-sm font-semibold ${index.trend === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {index.change > 0 ? '+' : ''}{index.change.toFixed(2)} ({index.change_percent > 0 ? '+' : ''}{index.change_percent.toFixed(2)}%)
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Market Movers */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Top Gainers */}
          <Card className="glass p-6 dark:bg-gray-800">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
              Top Gainers
            </h3>
            <div className="space-y-3">
              {marketMovers.top_gainers.slice(0, 5).map((stock, idx) => (
                <div key={idx} className="p-3 bg-white dark:bg-gray-700 rounded-lg">
                  <p className="font-semibold text-gray-800 dark:text-gray-100 text-sm">{stock.name}</p>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-gray-600 dark:text-gray-400 text-sm">₹{stock.price}</span>
                    <span className="text-green-600 font-semibold text-sm">+{stock.change_percent}%</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Top Losers */}
          <Card className="glass p-6 dark:bg-gray-800">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
              <TrendingDown className="w-5 h-5 mr-2 text-red-600" />
              Top Losers
            </h3>
            <div className="space-y-3">
              {marketMovers.top_losers.slice(0, 5).map((stock, idx) => (
                <div key={idx} className="p-3 bg-white dark:bg-gray-700 rounded-lg">
                  <p className="font-semibold text-gray-800 dark:text-gray-100 text-sm">{stock.name}</p>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-gray-600 dark:text-gray-400 text-sm">₹{stock.price}</span>
                    <span className="text-red-600 font-semibold text-sm">{stock.change_percent}%</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Most Active */}
          <Card className="glass p-6 dark:bg-gray-800">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
              <Sparkles className="w-5 h-5 mr-2 text-blue-600" />
              Most Active
            </h3>
            <div className="space-y-3">
              {marketMovers.most_active.slice(0, 5).map((stock, idx) => (
                <div key={idx} className="p-3 bg-white dark:bg-gray-700 rounded-lg">
                  <p className="font-semibold text-gray-800 dark:text-gray-100 text-sm">{stock.name}</p>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-gray-600 dark:text-gray-400 text-sm">₹{stock.price}</span>
                    <span className={`font-semibold text-sm ${stock.change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {stock.change_percent >= 0 ? '+' : ''}{stock.change_percent}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Sector Performance */}
        <Card className="glass p-6 mb-8 dark:bg-gray-800">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Sector Performance</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {sectors.map((sector, idx) => (
              <div
                key={idx}
                className="p-4 bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div className="text-3xl mb-2">{sector.icon}</div>
                <p className="font-semibold text-gray-800 dark:text-gray-100 text-sm">{sector.name}</p>
                <p className={`text-lg font-bold mt-1 ${sector.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                  {sector.change_percent >= 0 ? '+' : ''}{sector.change_percent}%
                </p>
              </div>
            ))}
          </div>
        </Card>

        {/* Quick Access Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="glass p-6 dark:bg-gray-800 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/stock-screener')}>
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center flex-shrink-0">
                <Filter className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">Stock Screener</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Browse and filter stocks across 7 categories with advanced search and sorting options
                </p>
                <Button variant="outline" size="sm" className="dark:bg-gray-700 dark:text-gray-100">
                  Explore Stocks →
                </Button>
              </div>
            </div>
          </Card>

          <Card className="glass p-6 dark:bg-gray-800 hover:shadow-lg transition-shadow">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center flex-shrink-0">
                <Star className="w-6 h-6 text-white fill-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">My Watchlist</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {watchlist.length === 0 
                    ? "Start tracking your favorite stocks by adding them to your watchlist"
                    : `${watchlist.length} stock${watchlist.length !== 1 ? 's' : ''} tracked • ${watchlist.filter(s => s.change_percent >= 0).length} up today`
                  }
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="dark:bg-gray-700 dark:text-gray-100"
                  onClick={() => {
                    if (watchlist.length > 0) {
                      document.getElementById('watchlist-section')?.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      navigate('/stock-screener');
                    }
                  }}
                >
                  {watchlist.length === 0 ? 'Browse Stocks' : 'View Watchlist'} →
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Educational Content */}
        <Card className="glass p-6 mb-8 dark:bg-gray-800">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6 flex items-center">
            <BookOpen className="w-5 h-5 mr-2 text-purple-600 dark:text-purple-400" />
            Investment Guide
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-5 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-700">
              <div className="text-3xl mb-3">📖</div>
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">What is Nifty 50?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Learn about India's benchmark stock market index and how it works.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openEducationModal('nifty50')}
                className="dark:bg-gray-700 dark:text-gray-100"
              >
                Learn More
              </Button>
            </div>
            
            <div className="p-5 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-700">
              <div className="text-3xl mb-3">💡</div>
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">How to Pick Stocks?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Understand key metrics like P/E ratio, dividend yield, and fundamentals.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openEducationModal('pickStocks')}
                className="dark:bg-gray-700 dark:text-gray-100"
              >
                Learn More
              </Button>
            </div>
            
            <div className="p-5 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl border border-purple-200 dark:border-purple-700">
              <div className="text-3xl mb-3">🎯</div>
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">Diversification Tips</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Learn how to build a balanced portfolio across sectors.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openEducationModal('diversification')}
                className="dark:bg-gray-700 dark:text-gray-100"
              >
                Learn More
              </Button>
            </div>
          </div>
        </Card>

      </div>

      {/* Educational Modal */}
      <Dialog open={educationModalOpen} onOpenChange={setEducationModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto dark:bg-gray-800">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center">
              <BookOpen className="w-6 h-6 mr-2 text-purple-600 dark:text-purple-400" />
              {educationContent.title}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {educationContent.content}
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
